import React, { useState, useEffect } from 'react';
import { useQuery, gql, useReactiveVar } from '@apollo/client';
import { Dropdown } from '@statsbomb/kitbag-components';
import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import {
  API_PLAYER_AGGREGATE_MODES,
  API_PLAYER_GROUP_BY,
} from '../../../utils/constants/api';
import { GENERAL_POSITION_TO_TILE_SUB_CATEGORY } from '../../League/PlayerStats/PlayerStats.constants';
import {
  SummaryTile,
  SummaryTileBody,
  SummaryTileCol,
  SummaryTileFooter,
  SummaryTileHeader,
} from '../../../components/Tile/TileSummary.styles';
import { SORT_DIRECTIONS } from '../../../utils/constants/sortDirections';
import TabbedTable from '../../../components/Tables/TabbedTable/TabbedTable';
import GoTo from '../../../components/buttons/GoTo';
import { getPlayerStats } from './getPlayerStats';
import { orderStats } from '../../../utils/helpers/stats.dataManipulation';
import {
  mf_AllSeasons,
  mf_PlayerDetails,
  mf_Seasons,
  mf_Teams,
} from '../../../apollo';
import { PlayerStatsTableWrapper } from '../../League/PlayerStats/PlayerStats.styles';
import { OverviewTileMessage } from './PlayerOverview.styles';
import useQueryString from '../../../utils/hooks/useQueryString';
import { getQueryStringValue } from '../../../utils/queryString';
import {
  SingleValue,
  Option,
} from '../../../components/DropdownFilter/DropdownFilter.components';
import {
  DEFAULT_HIGHLIGHTED_COLUMN,
  DEFAULT_SORT_BY,
  PERMANENT_HEADERS,
  STATS_AGGREGATION_MODE_OPTIONS,
} from './PlayerStats.Tile.constants';
import { useGetPlayerStatDefinitions } from '../../League/PlayerStats/PlayerStats.hooks';
import { useIsNcaa } from '../../../utils/hooks/useIsNcaa';
import { toUsDate } from '../../../utils/helpers/formatting';
import { useGetPlayerStatsCategoryDefinitions } from '../../../utils/hooks/useGetPlayerStatCategories';
import { DropdownWrapper } from '../../../components/DropdownFilter/Dropdown.styles';
import LoaderInline from '../../../components/LoaderInline/LoaderInline';
import {
  USER_GROUP,
  USER_ROLES,
  VIDEO_JWT_CLAIMS,
} from '../../../utils/constants/auth';
import LinkButton from '../../../components/buttons/LinkButton/LinkButton';
import { getRosterPositionByCode } from '../../../utils/helpers/positions';
import { DEFAULT_SCOUT_POSITION } from '../../Scout/ScoutResults/ScoutResults.constants';
import {
  formatPlayerVideoStatValues,
  getPlayerVideoStatHeaders,
  getPositionCategoryDefinitions,
  shouldHideParentTabs,
} from './PlayerStats.dataManipulation';
import { useStore } from '../../../store/useStore';
import VideoModal from '../../../components/Video/VideoModal';

const { DESCENDING, ASCENDING } = SORT_DIRECTIONS;

function PlayerStatsTile() {
  const setVideoModalOpen = useStore((s) => s.setVideoModalOpen);
  // selected player
  const mf_PlayerDetailsRV = useReactiveVar(mf_PlayerDetails);
  const mf_TeamsRV = useReactiveVar(mf_Teams);
  const isNcaa = useIsNcaa();
  const playerName = mf_PlayerDetailsRV?.name;
  const [aggMode, setAggMode] = useQueryString(
    'agg',
    API_PLAYER_AGGREGATE_MODES.TOTAL,
  );
  const generalPosition =
    mf_PlayerDetailsRV?.mostCommonPosition?.generalPosition;

  const defaultSubcategory =
    GENERAL_POSITION_TO_TILE_SUB_CATEGORY[generalPosition];

  const [selectedSubcategory, setSelectedSubcategory] =
    useState(defaultSubcategory);
  // parent category tab, only shown when a position has more than one category.
  // TabbedTable persists the active tab in the `tab` query string, so start from it
  const [selectedParentCategory, setSelectedParentCategory] = useState(
    getQueryStringValue('tab'),
  );
  const [sortDirection, setSortDirection] = useState(ASCENDING);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [highlightedColumn, setHighlightedColumn] = useState(
    DEFAULT_HIGHLIGHTED_COLUMN,
  );
  // video authorisation
  const { user } = useKitbagAuth();
  const isAuthorised =
    user['https://statsbomb.com/accountID'] === USER_GROUP.STATSBOMB ||
    user[VIDEO_JWT_CLAIMS]?.roles.includes(USER_ROLES.VIDEO_CUSTOMER);

  const { categoryDefinitions, isLoading: isLoadingCategoryDefinitions } =
    useGetPlayerStatsCategoryDefinitions();

  const allowedCategoryDefinitions = getPositionCategoryDefinitions(
    generalPosition,
    categoryDefinitions,
  );

  const selectedCategoryDefinition =
    allowedCategoryDefinitions.find(
      (category) => category.name === selectedParentCategory,
    ) || allowedCategoryDefinitions[0];

  const orderedSubcategoryStats = (() => {
    const stats = selectedCategoryDefinition?.subcategories.find(
      (subcategory) => subcategory.name === selectedSubcategory,
    )?.stats;
    return stats ? ['plays', ...stats] : undefined;
  })();

  const isRateMode = aggMode === API_PLAYER_AGGREGATE_MODES.RATE;
  const { statDefinitions, isLoadingStatDefinitions } =
    useGetPlayerStatDefinitions(orderedSubcategoryStats, isRateMode);

  // get all subcategories of the selected category
  const subcategories = selectedCategoryDefinition?.subcategories.map(
    (subcategory) => subcategory.name,
  );

  // set default subcategory once the category has been set
  useEffect(() => {
    if (subcategories?.length) {
      setSelectedSubcategory(defaultSubcategory);
    }
  }, [selectedCategoryDefinition?.name]);

  const playerStatsQuery = getPlayerStats(statDefinitions);
  const { data: playerStatsData, loading: isLoadingPlayerStats } = useQuery(
    gql(playerStatsQuery),
    {
      skip: !statDefinitions?.length,
      variables: {
        aggMode,
        groupByMode: API_PLAYER_GROUP_BY.GAME,
        orderBy: [
          {
            name: sortBy,
            descending: sortDirection === DESCENDING,
          },
        ],
      },
    },
  );

  const { data: playerStatsTotalData, loading: isLoadingPlayerStatsTotal } =
    useQuery(gql(playerStatsQuery), {
      skip: !statDefinitions?.length,
      variables: {
        aggMode,
        groupByMode: API_PLAYER_GROUP_BY.PLAYER,
      },
    });

  const playerStats = playerStatsData?.playerStats.items.map((player) => ({
    ...player,
    gameDate: toUsDate(player.gameDate),
    gameId: player.groups?.find((g) => g.key === 'GAME')?.value,
    season: mf_AllSeasons()?.find((season) => season.id === mf_Seasons())?.name,
  }));

  const playerStatsTotal = playerStatsTotalData?.playerStats.items;

  const headers = [
    ...PERMANENT_HEADERS,
    ...getPlayerVideoStatHeaders(statDefinitions),
  ];
  const headerIds = headers.map((header) => header.id);

  const formattedStats = formatPlayerVideoStatValues(
    playerStats,
    headerIds,
    statDefinitions,
    aggMode,
    headers,
  );

  const orderedStats = orderStats(formattedStats, headerIds);

  const formattedStatsTotal = formatPlayerVideoStatValues(
    playerStatsTotal,
    headerIds,
    statDefinitions,
    aggMode,
    headers,
  );

  if (formattedStatsTotal && formattedStatsTotal.length > 0) {
    formattedStatsTotal[0].gameDate = mf_AllSeasons()?.find(
      (season) => season.id === mf_Seasons(),
    )?.name;
    formattedStatsTotal[0].gameName = '';
  }
  const orderedStatsTotal = orderStats(formattedStatsTotal, headerIds);

  const stickyColumns = [
    { index: 1, width: 5.625 },
    { index: 2, width: isNcaa ? 6.25 : 5.625 },
    { index: 3, width: 3.75 },
  ];

  // Build tab config only for the categories allowed for this position.
  const playerStatsConfig = allowedCategoryDefinitions.map((category) => ({
    id: category.name,
    label: category.name,
    tableConfig: {
      headers,
      footer: orderedStatsTotal?.map((stat) => {
        const { gameId, ...rest } = stat;
        return rest;
      }),
      rows: orderedStats,
      stickyColumns,
      maxHeight: 353,
    },
  }));

  const hideParentTabs = shouldHideParentTabs(
    generalPosition,
    allowedCategoryDefinitions.length,
  );

  const loadingMsg = () => {
    if (isLoadingStatDefinitions || isLoadingCategoryDefinitions) {
      return 'Loading Stat Definitions';
    }

    if (isLoadingPlayerStats) {
      return 'Loading Game Data';
    }

    if (isLoadingPlayerStatsTotal) {
      return 'Loading Season Total Data';
    }

    return '';
  };

  const handleTabChange = (e) => {
    setSelectedSubcategory(e.target.innerText);
    setSortBy(DEFAULT_SORT_BY);
    setHighlightedColumn(DEFAULT_HIGHLIGHTED_COLUMN);
  };

  // e.g. Rushing <-> Blocking for a RB; the subcategory resets via the effect above
  const handleParentTabChange = (_e, tab) => {
    setSelectedParentCategory(tab.value);
    setSortBy(DEFAULT_SORT_BY);
    setHighlightedColumn(DEFAULT_HIGHLIGHTED_COLUMN);
  };

  const handleSort = (statName) => {
    if (statName === sortBy) {
      setSortDirection(sortDirection === ASCENDING ? DESCENDING : ASCENDING);
    } else {
      setSortDirection(DESCENDING);
      setSortBy(statName);
      setHighlightedColumn(
        headerIds.filter((stat) => stat !== 'gameId').indexOf(statName) + 1,
      );
    }
  };

  const isLoading =
    isLoadingCategoryDefinitions ||
    isLoadingPlayerStats ||
    isLoadingStatDefinitions ||
    isLoadingPlayerStatsTotal;

  const showVideoLinks =
    mf_PlayerDetailsRV?.mostCommonPosition?.code !== 'K' &&
    mf_PlayerDetailsRV?.mostCommonPosition?.code !== 'P';

  return (
    <>
      <SummaryTile>
        <SummaryTileHeader>
          <div>
            <h3>Stats</h3>
          </div>
          <div>
            {playerStatsData && !isLoading && playerName && (
              <DropdownWrapper>
                <Dropdown
                  id="statsAggregationDrop"
                  components={{
                    Option,
                    SingleValue,
                  }}
                  isSearchable={false}
                  menuAlignment="right"
                  menuPlacement="bottom"
                  onChange={(item) => setAggMode(item.value)}
                  options={STATS_AGGREGATION_MODE_OPTIONS}
                  value={
                    STATS_AGGREGATION_MODE_OPTIONS[
                      aggMode === API_PLAYER_AGGREGATE_MODES.TOTAL ? 1 : 0
                    ]
                  }
                  label="Stats aggregation mode"
                  labelPosition="none"
                  variant="tertiary"
                  size="small"
                />
              </DropdownWrapper>
            )}
            <GoTo
              href="/league/stats/players/:leagues/:seasons"
              linkName="Open league stats"
              iconSize="small"
            />
          </div>
        </SummaryTileHeader>
        <SummaryTileBody>
          {playerStatsData && playerStatsTotalData && playerName && (
            <PlayerStatsTableWrapper>
              <TabbedTable
                tableTabsConfig={playerStatsConfig}
                isLoading={isLoading}
                subTabs={subcategories}
                selectedSubTab={selectedSubcategory}
                onSubTabChange={handleTabChange}
                onTabChange={handleParentTabChange}
                sortDirection={sortDirection}
                sortBy={sortBy}
                highlightedColumn={highlightedColumn}
                handleSort={handleSort}
                withBorder={false}
                withPadding={false}
                hideParentTabs={hideParentTabs}
                withBorderOuter
                isHeadSticky
                nonTableHeight="392px"
                renderClickableStatCell={(cell, rowIndex, rows) =>
                  isAuthorised && showVideoLinks ? (
                    <LinkButton
                      onClickFn={() => {
                        setVideoModalOpen({
                          videoModalOpen: true,
                          selectedStatName: cell.name,
                          selectedPlayerId: mf_PlayerDetailsRV?.id,
                          selectedTeamId: mf_TeamsRV,
                          selectedGameId: rows[rowIndex].gameId.label,
                          selectedTitle: `${mf_PlayerDetailsRV?.name}, ${
                            statDefinitions.find((s) => s.name === cell.name)
                              .prettyName
                          }`,
                          selectedPosition: getRosterPositionByCode(
                            mf_PlayerDetailsRV?.mostCommonPosition?.code,
                          ).apiCode,
                        });
                      }}
                      tooltip="Watch video"
                      label={cell.label}
                      testId={`link-button-${cell.id}-${rowIndex}`}
                    />
                  ) : (
                    cell.label
                  )
                }
                renderClickableStatFooterCell={(cell, rowIndex) =>
                  isAuthorised ? (
                    <LinkButton
                      onClickFn={() => {
                        setVideoModalOpen({
                          videoModalOpen: true,
                          selectedStatName: cell.name,
                          selectedPlayerId: mf_PlayerDetailsRV?.id,
                          selectedTeamId: mf_TeamsRV,
                          selectedTitle: `${mf_PlayerDetailsRV?.name}, ${
                            statDefinitions.find((s) => s.name === cell.name)
                              .prettyName
                          }`,
                          selectedPosition: getRosterPositionByCode(
                            mf_PlayerDetailsRV?.mostCommonPosition?.code,
                          ).apiCode,
                        });
                      }}
                      tooltip="Watch video"
                      label={cell.label}
                      testId={`link-button-${cell.id}-${rowIndex}`}
                    />
                  ) : (
                    cell.label
                  )
                }
              />
            </PlayerStatsTableWrapper>
          )}
          {!playerStatsData && !isLoading && playerName && (
            <OverviewTileMessage>
              <p>No Data Available.</p>
            </OverviewTileMessage>
          )}
        </SummaryTileBody>
        <SummaryTileFooter $transparentBackground={!isLoading}>
          <SummaryTileCol>
            {isLoading && <LoaderInline text={loadingMsg()} />}
          </SummaryTileCol>
          <SummaryTileCol />
        </SummaryTileFooter>
      </SummaryTile>
      <VideoModal
        onClose={() => {
          setVideoModalOpen({
            videoModalOpen: false,
            selectedStatName: '',
            selectedPlayerId: '',
            selectedTeamId: '',
            selectedGameId: '',
            selectedTitle: '',
            selectedPosition: DEFAULT_SCOUT_POSITION,
          });
        }}
      />
    </>
  );
}

export default PlayerStatsTile;
